import { useState } from 'react'
import {
  DefaultTitle,
  FormContainer,
  PDFApplicationCard,
  Link
} from '../../../components/application/defaultComponents'
import ReminderCard from 'src/components/basic/ReminderCard'
import { FlexDiv } from 'src/components/application/formComponents/styles'
import { InsuranceTypeData } from '../../../dtos/applicationData'
import StandardInput from '../../../components/basic/StandardInput'
import Button from 'src/components/basic/Button'
import { tlieSources } from 'src/constants/tlie'
import { api } from 'src/services/apiClient'
import { HiDownload } from 'react-icons/hi'

const PdfApplication = ({ insuranceType }: { insuranceType: InsuranceTypeData }) => {
  const [email, setEmail] = useState('')
  const [isSendingEmail, setIsSendingEmail] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [emailSuccess, setEmailSuccess] = useState<boolean | undefined>(undefined)

  const handleSendEmail = async () => {
    setIsSendingEmail(true)
    setEmailSuccess(undefined)
    try {
      await api.post(`/Applications/SendPdfApplication?email=${email}&type=${insuranceType === 'LAWYER' ? 0 : 1}`)
      setEmailSuccess(true)
    } catch(_) {
      setEmailSuccess(false)
    } finally {
      setIsSendingEmail(false)
    }
  }

  const handleDownloadApplication = async () => {
    setIsDownloading(true)
    try {
      const { data } = await api.post(`/Applications/DownloadPdfApplication?type=${insuranceType === 'LAWYER' ? 0 : 1}`, {}, { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'TLIE New Business.pdf')
      link.click()
      window.URL.revokeObjectURL(url)
    } catch(_) {} finally {
      setIsDownloading(false)
    }
  }
  
  return (
    <>
      <DefaultTitle>Complete a PDF application</DefaultTitle>
      <FormContainer maxWidth='100%'>
        <PDFApplicationCard>
          <p>
            These forms can be filled out in Adobe Reader, 
            signed and saved without an additional editing program, 
            and then emailed to TLIE at {' '}
            <Link href={`mailto:${tlieSources.applicationEmail}`}>{tlieSources.applicationEmail}</Link>.
          </p>
          <p>
            Forms must include a signature. Facsimilie and digital signatures 
            are acceptable. You can fill out the forms on your computer, 
            scan them after printing with a manual signature, or fill them out 
            by hand. You can send completed forms to <Link>{tlieSources.applicationEmail}</Link>, 
            fax them to <Link>{tlieSources.applicationFax}</Link> or send by regular mail.
          </p>
          <p>
            To get the best results with PDF forms,{' '}
            <Link href='https://get.adobe.com/br/reader/' rel="noreferrer noopener" target="_blank">click here</Link>
            {' '}to get the latest version of Adobe Reader. For more information on generating 
            your own digital signature in Adobe Reader, {' '}
            <Link
              href='https://cufacilities.sites.clemson.edu/digitalSig.php'
              rel="noreferrer noopener"
              target="_blank"
            >
              click here
            </Link>
          </p>
        </PDFApplicationCard>
        <FlexDiv gap='14px' margin='32px 0 0 0'>
          <FlexDiv>
            <StandardInput 
              label="Email Address"
              name="email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
          </FlexDiv>
          <FlexDiv minWidth='250px' align='flex-end'>
            <Button
              variant="secondary"
              style={{ padding: '5px 36px' }}
              onClick={handleSendEmail}
              disabled={isSendingEmail}
            >
              Email me an application
            </Button>
          </FlexDiv>
        </FlexDiv>
        {emailSuccess !== undefined ? (
          <FlexDiv margin="24px 0 0 0" maxWidth='660px'>
            <ReminderCard
              title={emailSuccess === true ? 'E-mail sent!' : 'Error trying to send e-mail!'}
              icon={emailSuccess === true ? 'bulb' : 'warning'}
            >
              {
                emailSuccess === true ? 
                'We have sent you the pdf file of the application. Please check your inbox to download the attachment.' :
                'An error has ocurred while trying to send the e-mail. Please try again.'
              }
            </ReminderCard>
          </FlexDiv>
        ) : null}
        <FlexDiv margin='32px 0 0 0'>
          <Button
            variant='secondary'
            onClick={handleDownloadApplication}
            disabled={isDownloading}
          >
            <HiDownload style={{marginRight: '8px'}} />
            Download an application
          </Button>
        </FlexDiv>
      </FormContainer>
    </>
  )
}

export default PdfApplication